import { useQuery } from '@tanstack/react-query';
import {
	isUnauthorizedApiError,
	makeRequest,
} from '@dealstack/frontend/shared/dataExchange/ApiLayer/apiLayer';
import { HTTP_METHOD } from '@dealstack/shared/lib/common/dataExchange/common';
import API_PATHS from '@dealstack/shared/lib/generated/apiPaths';
import type { ProgrammeSigningDocumentRow } from './ProgrammeSigningPage.types';

interface ProgrammeSigningDocumentsResponse {
	payload: {
		documents: ProgrammeSigningDocumentRow[];
	};
}

/**
 * Retry policy for the programme-vs-signature token probe: a 401 is the
 * definitive "wrong scope" answer and must fail fast (no retry), while any
 * other failure is transient and worth a couple of retries before we treat the
 * probe as failed. Exported so the policy can be unit-tested directly.
 */
export const shouldRetryProgrammeProbe = (failureCount: number, error: unknown): boolean =>
	!isUnauthorizedApiError(error) && failureCount < 2;

export const useProgrammeSigningDocuments = (token: string) => {
	return useQuery({
		queryKey: ['programme-signing-documents', token],
		queryFn: () =>
			makeRequest<ProgrammeSigningDocumentsResponse>(
				{
					endpoint: API_PATHS.PUBLIC_SIGNING_PROGRAMME_DOCUMENTS_GET,
					method: HTTP_METHOD.GET,
					queryString: { token },
				},
				// The visitor is unauthenticated — a 401 must surface as a query
				// error, not the default redirect to the login page.
				{ skipLoginRedirect: true }
			),
		select: (response) => response.payload.documents,
		enabled: !!token,
		// PublicSigningPage uses this query's 401 as the programme-vs-signature
		// token probe, so a 401 must resolve immediately (no retries — that would
		// leave single-document signers on a spinner) and must not surface the
		// cache-level generic error toast. A NON-401 failure (transient/network)
		// is not a signature token, so retry it a couple of times before giving
		// up — otherwise a blip would misclassify a valid programme visitor.
		retry: shouldRetryProgrammeProbe,
		meta: { suppressErrorToast: true },
	});
};
